'use client';

import { useState, useEffect, useCallback } from 'react';
import { clientTransactionsApi } from '../api';
import { useLiveSync } from '@/lib/live-sync';
import type { ClientTransactionsStats } from '../types';

export function useTransactionsStats(tenantSlug: string) {
  const [stats, setStats] = useState<ClientTransactionsStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    if (!tenantSlug) return;

    try {
      setError(null);
      const data = await clientTransactionsApi.getClientTransactionsStats(tenantSlug);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch transaction stats'));
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug]);

  useLiveSync({
    onSync: fetchStats,
    scope: ['transactions', 'wallet'],
    intervalMs: 20000,
    enabled: !!tenantSlug,
  });

  useEffect(() => {
    setIsLoading(true);
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    isLoading,
    error,
    refetch: fetchStats,
  };
}
